/**
 * Shift-specific hooks using TanStack Query
 * Provides data fetching, caching, and mutations for shift operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { ShiftsService } from '@/api/services/ShiftsService';
import { shifts_CopyWeekRequest } from '@/api/models/shifts_CopyWeekRequest';
import type { shifts_BulkPublishRequest } from '@/api/models/shifts_BulkPublishRequest';
import type { shifts_BulkPublishResponse } from '@/api/models/shifts_BulkPublishResponse';
import type { shifts_CountByStatusResponse } from '@/api/models/shifts_CountByStatusResponse';
import type { shifts_PublishPreviewResponse } from '@/api/models/shifts_PublishPreviewResponse';
import type {
  Shift,
  ShiftListParams,
  CreateShiftRequest,
  UpdateShiftRequest,
  UpdateShiftSeriesRequest,
  AssignGroupsRequest,
  CheckConflictsRequest,
  PatternResponse,
} from '@/types/shifts';
import { ApiError } from '@/api';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import type { I18n } from '@lingui/core';
import { errorToastText, extractBackendError } from '@/lib/translateBackendError';
import { createShiftToast, seriesUpdateToast } from './shiftSeriesToast';

/**
 * Backend codes of the 409s a shift dialog shows inline (#2119): the overlap, the absence and
 * the optimistic lock. A dialog that renders them passes `inlineConflicts`, and the hook does
 * not toast them a second time.
 */
export const INLINE_CONFLICT_CODES = ['SHIFT_CONFLICT', 'EMPLOYEE_ABSENT', 'OPTIMISTIC_LOCK'] as const;

/** Whether a failed shift write is one of the 409s a dialog shows inline. */
export function isInlineConflict(error: unknown): boolean {
  const { code } = extractBackendError(error);
  return !!code && (INLINE_CONFLICT_CODES as readonly string[]).includes(code);
}

interface ShiftWriteOptions {
  /** The caller shows SHIFT_CONFLICT, EMPLOYEE_ABSENT and OPTIMISTIC_LOCK itself. */
  inlineConflicts?: boolean;
  /** First name per employee id, for the toast of a multi-employee create (#2121). */
  nameOf?: (employeeId: string) => string;
}

const shiftErrorMessages = {
  conflict: msg({ id: 'shifts.toast.conflictError', message: 'Shift overlaps with existing shift' }),
  notFound: msg({ id: 'shifts.toast.notFoundError', message: 'Shift not found' }),
  optimisticLock: msg({
    id: 'shifts.toast.optimisticLockError',
    message: 'Shift was modified in the meantime',
  }),
  invalidRecurrence: msg({
    id: 'shifts.toast.invalidRecurrenceError',
    message: 'Invalid recurrence settings',
  }),
  generic: msg({ id: 'shifts.toast.error', message: 'Error performing shift operation' }),
};

// The recurrence checks in the shift handler answer 400 with a message and no
// code; these are their texts.
const RECURRENCE_MESSAGES = /invalid recurrence|repetitions/i;

/**
 * Toast text for a failed shift write, decided by the backend code. The shift
 * codes get the shift wording; every other code (EMPLOYEE_ABSENT,
 * SHIFT_WEEKDAYS_REQUIRED, ...) and a client-safe body message go through
 * errorToastText.
 */
function shiftErrorText(error: unknown, i18n: I18n, options?: { recurrence?: boolean }): string {
  const { code, message } = extractBackendError(error);
  switch (code) {
    case 'SHIFT_CONFLICT':
      return i18n._(shiftErrorMessages.conflict);
    case 'SHIFT_NOT_FOUND':
      return i18n._(shiftErrorMessages.notFound);
    case 'OPTIMISTIC_LOCK':
      return i18n._(shiftErrorMessages.optimisticLock);
  }
  if (options?.recurrence && !code && message && RECURRENCE_MESSAGES.test(message)) {
    return i18n._(shiftErrorMessages.invalidRecurrence);
  }
  return errorToastText(error, i18n, shiftErrorMessages.generic);
}

// Request/response types for copy week operation
export interface CopyWeekApiRequest {
  source_start_date: string; // YYYY-MM-DD (Monday)
  target_start_date: string; // YYYY-MM-DD (Monday)
  target_week_count: number; // 1-12
  employee_ids?: string[];
  group_ids?: string[];
  conflict_strategy: shifts_CopyWeekRequest.conflict_strategy;
}

export interface CopyWeekApiResponse {
  copied_count: number;
  skipped_count: number;
  overwrite_count: number;
}

// Query keys for shifts
export const shiftKeys = {
  all: ['shifts'] as const,
  lists: () => [...shiftKeys.all, 'list'] as const,
  list: (params: ShiftListParams) => [...shiftKeys.lists(), params] as const,
  details: () => [...shiftKeys.all, 'detail'] as const,
  detail: (id: string) => [...shiftKeys.details(), id] as const,
  patterns: () => [...shiftKeys.all, 'patterns'] as const,
  pattern: (patternId: string) => [...shiftKeys.patterns(), patternId] as const,
  countByStatus: (fromDate: string, toDate: string, status: string) =>
    [...shiftKeys.all, 'countByStatus', fromDate, toDate, status] as const,
  publishPreview: (fromDate: string) => [...shiftKeys.all, 'publishPreview', fromDate] as const,
};

/**
 * Hook to fetch paginated shifts with filters.
 *
 * @param params - Filter parameters for shifts query
 * @returns TanStack Query result with shift list data
 */
export function useShifts(
  params: ShiftListParams = {},
  options?: { enabled?: boolean }
) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: shiftKeys.list(params),
    queryFn: () =>
      ShiftsService.getApiV1Shifts(
        params.limit ?? 100,
        params.offset,
        params.employeeId,
        params.groupId,
        params.fromDate,
        params.toDate,
        params.status
      ),
    // Callers may DISABLE the query (e.g. a childcare that resolves to zero
    // groups must show an empty table, not fetch org-wide). The override only
    // narrows: auth must still be ready before any fetch fires.
    enabled: (options?.enabled ?? true) && isSignedIn && isAuthReady,
    staleTime: 30000, // 30 seconds
    retry: (failureCount, error) => {
      // Don't retry on authentication/authorization errors
      if (error instanceof ApiError) {
        if (error.status === 401 || error.status === 403) {
          return false;
        }
      }
      // Fallback for non-ApiError errors: check message strings
      if (
        error instanceof Error &&
        (error.message.includes('401') ||
          error.message.includes('403') ||
          error.message.includes('Unauthorized'))
      ) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to fetch a single shift by ID.
 *
 * @param id - Shift ID
 * @param options - Optional query options
 * @returns TanStack Query result with shift data
 */
export function useShift(id: string, options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: shiftKeys.detail(id),
    queryFn: () => ShiftsService.getApiV1Shifts1(id),
    enabled: (options?.enabled ?? true) && !!id && isSignedIn && isAuthReady,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to create a new shift.
 *
 * @returns Mutation function and state
 */
export function useCreateShift(options?: ShiftWriteOptions) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (request: CreateShiftRequest) =>
      ShiftsService.postApiV1Shifts(request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: shiftKeys.lists() });
      const { title, description } = createShiftToast(data, i18n, options?.nameOf);
      toast.success(title, description ? { description } : undefined);
    },
    onError: (error) => {
      if (options?.inlineConflicts && isInlineConflict(error)) return;
      toast.error(shiftErrorText(error, i18n));
    },
  });
}

/**
 * Hook to update an existing shift.
 *
 * @returns Mutation function and state
 */
export function useUpdateShift(options?: ShiftWriteOptions) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdateShiftRequest }) =>
      ShiftsService.putApiV1Shifts(id, request),
    onSuccess: (data: Shift, variables) => {
      queryClient.invalidateQueries({ queryKey: shiftKeys.lists() });
      queryClient.setQueryData(shiftKeys.detail(variables.id), data);
      toast.success(
        i18n._({
          id: 'shifts.toast.updateSuccess',
          message: 'Shift updated',
        })
      );
    },
    onError: (error) => {
      if (options?.inlineConflicts && isInlineConflict(error)) return;
      toast.error(shiftErrorText(error, i18n));
    },
  });
}

/**
 * Hook to delete a shift.
 *
 * @returns Mutation function and state
 */
export function useDeleteShift() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (id: string) => ShiftsService.deleteApiV1Shifts(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: shiftKeys.lists() });
      queryClient.removeQueries({ queryKey: shiftKeys.detail(id) });
      toast.success(
        i18n._({ id: 'shifts.toast.deleteSuccess', message: 'Shift deleted' })
      );
    },
    onError: (error) => {
      toast.error(shiftErrorText(error, i18n));
    },
  });
}

/**
 * Hook to check for shift conflicts.
 *
 * @returns Mutation function and state
 */
export function useCheckShiftConflicts() {
  return useMutation({
    mutationFn: (request: CheckConflictsRequest) =>
      ShiftsService.postApiV1ShiftsCheckConflicts(request),
  });
}

/**
 * Hook to assign groups to a shift.
 *
 * @returns Mutation function and state
 */
export function useAssignShiftGroups() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: AssignGroupsRequest }) =>
      ShiftsService.putApiV1ShiftsGroups(id, request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: shiftKeys.lists() });
      queryClient.invalidateQueries({ queryKey: shiftKeys.detail(variables.id) });
      toast.success(
        i18n._({
          id: 'shifts.toast.groupsAssignedSuccess',
          message: 'Groups assigned',
        })
      );
    },
    onError: (error) => {
      toast.error(shiftErrorText(error, i18n));
    },
  });
}

/**
 * Hook to fetch shift pattern details for series operations.
 *
 * @param patternId - Pattern ID
 * @param options - Optional query options
 * @returns TanStack Query result with pattern data
 */
export function useShiftPattern(
  patternId: string,
  options?: { enabled?: boolean }
) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery<PatternResponse>({
    queryKey: shiftKeys.pattern(patternId),
    queryFn: () => ShiftsService.getApiV1ShiftsSeries(patternId),
    enabled: !!patternId && isSignedIn && isAuthReady && options?.enabled !== false,
    staleTime: 30000, // 30 seconds
    retry: (failureCount, error) => {
      // Don't retry on authentication/authorization errors or not found
      if (error instanceof ApiError) {
        if (
          error.status === 401 ||
          error.status === 403 ||
          error.status === 404
        ) {
          return false;
        }
      }
      // Fallback for non-ApiError errors: check message strings
      if (
        error instanceof Error &&
        (error.message.includes('401') ||
          error.message.includes('403') ||
          error.message.includes('404') ||
          error.message.includes('Unauthorized') ||
          error.message.includes('not found'))
      ) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to delete a shift series.
 *
 * @returns Mutation function and state
 */
export function useDeleteShiftSeries() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({
      patternId,
      futureOnly,
      fromDate,
    }: {
      patternId: string;
      futureOnly: boolean;
      fromDate?: string;
    }) => ShiftsService.deleteApiV1ShiftsSeries(patternId, futureOnly, fromDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shiftKeys.lists() });
      queryClient.invalidateQueries({ queryKey: shiftKeys.patterns() });
      toast.success(
        i18n._({
          id: 'shifts.toast.seriesDeleteSuccess',
          message: 'Series deleted',
        })
      );
    },
    onError: (error) => {
      toast.error(shiftErrorText(error, i18n));
    },
  });
}

/**
 * Hook to update shifts in a series with different scopes.
 *
 * @returns Mutation function and state
 */
export function useUpdateShiftSeries(options?: ShiftWriteOptions) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({
      patternId,
      shiftId,
      request,
    }: {
      patternId: string;
      shiftId: string;
      request: UpdateShiftSeriesRequest;
    }) => ShiftsService.putApiV1ShiftsSeriesShifts(patternId, shiftId, request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: shiftKeys.lists() });
      queryClient.invalidateQueries({ queryKey: shiftKeys.patterns() });
      if (data.id) {
        const { series: _summary, ...shift } = data;
        queryClient.setQueryData<Shift>(shiftKeys.detail(data.id), shift);
      }
      const { title, description } = seriesUpdateToast(data.series, i18n);
      toast.success(title, description ? { description } : undefined);
    },
    onError: (error) => {
      if (options?.inlineConflicts && isInlineConflict(error)) return;
      toast.error(shiftErrorText(error, i18n, { recurrence: true }));
    },
  });
}

/**
 * Hook to copy shifts from a source week to target weeks.
 *
 * @returns Mutation function and state
 */
export function useCopyWeek() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (request: CopyWeekApiRequest) =>
      ShiftsService.postApiV1ShiftsCopyWeek(request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: shiftKeys.lists() });
      const message = i18n._({
        id: 'shifts.copyWeek.success',
        message: 'Copied {copiedCount} shifts ({skippedCount} skipped)',
        values: { copiedCount: data.copied_count ?? 0, skippedCount: data.skipped_count ?? 0 },
      });
      toast.success(message);
    },
    onError: (error) => {
      toast.error(shiftErrorText(error, i18n));
    },
  });
}

/**
 * Hook to bulk publish DRAFT shifts in a date range.
 *
 * @returns Mutation function and state
 */
export function useBulkPublish(options?: {
  /** Offered as the toast action: opens the published range in the planner (#2038). */
  onShowRange?: (fromDate: string, toDate: string) => void;
}) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  const onShowRange = options?.onShowRange;

  return useMutation({
    mutationFn: (request: shifts_BulkPublishRequest) =>
      ShiftsService.postApiV1ShiftsBulkPublish(request),
    onSuccess: (data: shifts_BulkPublishResponse, request: shifts_BulkPublishRequest) => {
      queryClient.invalidateQueries({ queryKey: shiftKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['shifts', 'countByStatus'] });
      toast.success(
        i18n._({
          id: 'shifts.publish.success',
          message: 'Published {count} shifts',
          values: { count: data.count },
        }),
        onShowRange
          ? {
              action: {
                label: i18n._({ id: 'shifts.publish.showRange', message: 'Show period' }),
                onClick: () => onShowRange(request.from_date, request.to_date),
              },
            }
          : undefined
      );
    },
    onError: (error) => {
      toast.error(shiftErrorText(error, i18n));
    },
  });
}

/**
 * Hook to bulk unpublish PUBLISHED shifts back to DRAFT in a date range.
 *
 * @returns Mutation function and state
 */
export function useBulkUnpublish() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (request: shifts_BulkPublishRequest) =>
      ShiftsService.postApiV1ShiftsBulkUnpublish(request),
    onSuccess: (data: shifts_BulkPublishResponse) => {
      queryClient.invalidateQueries({ queryKey: shiftKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['shifts', 'countByStatus'] });
      toast.success(
        i18n._({
          id: 'shifts.unpublish.success',
          message: 'Unpublished {count} shifts',
          values: { count: data.count },
        })
      );
    },
    onError: (error) => {
      toast.error(shiftErrorText(error, i18n));
    },
  });
}

/**
 * Hook to count DRAFT shifts in a date range.
 *
 * @param fromDate - Start date (YYYY-MM-DD)
 * @param toDate - End date (YYYY-MM-DD)
 * @param status - Shift status to count (defaults to 'DRAFT')
 * @param options - Optional query options
 * @returns TanStack Query result with count data
 */
type ShiftStatus = 'DRAFT' | 'PUBLISHED' | 'CONFIRMED' | 'CANCELLED';

export function useCountShiftsByStatus(
  fromDate: string,
  toDate: string,
  status: ShiftStatus = 'DRAFT',
  options?: { enabled?: boolean }
) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery<shifts_CountByStatusResponse>({
    queryKey: shiftKeys.countByStatus(fromDate, toDate, status),
    queryFn: () =>
      ShiftsService.getApiV1ShiftsCountByStatus(fromDate, toDate, status),
    enabled:
      (options?.enabled ?? true) &&
      !!fromDate &&
      !!toDate &&
      isSignedIn &&
      isAuthReady,
    staleTime: 15000, // 15 seconds — shorter since counts change with publish actions
  });
}

/**
 * The publication lead time of a period starting on `fromDate` (#2042): days until it
 * starts, the org's lead time setting and whether that is late. Always refetched, so a
 * saved setting applies the next time the publish dialog opens.
 */
export function usePublishPreview(fromDate: string, options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery<shifts_PublishPreviewResponse>({
    queryKey: shiftKeys.publishPreview(fromDate),
    queryFn: () => ShiftsService.getApiV1ShiftsPublishPreview(fromDate),
    enabled: (options?.enabled ?? true) && !!fromDate && isSignedIn && isAuthReady,
    staleTime: 0,
  });
}
